export const CENTRAL_ROLE_COPY = {
  decedent: "Decedent",
  primaryContact: "Primary contact",
  vitalRecordInformant: "Informant",
} as const;

export const CENTRAL_ACTION_COPY = {
  approveArrangement: "Approve for paperwork",
  approveVitals: "Approve for form generation",
  completeSelectedTasks: "Complete selected tasks",
  createArrangementLink: "Create private arrangement link",
  createFamilyPortalLink: "Issue family portal link",
  createSignatureLink: "Create signature link",
  openCaseRecord: "Open case record",
} as const;

export const CENTRAL_STATE_COPY = {
  delivery: {
    delivered: "Delivered",
    failed: "Failed",
    queued: "Queued",
    sent: "Sent",
  },
  dependency: {
    staff: "Waiting on staff",
    family: "Waiting on family",
    vendor: "Waiting on vendor",
  },
  document: {
    approved: "Approved",
    archived: "Archived",
    draft: "Draft",
    needsChanges: "Needs changes",
    needsReview: "Needs review",
    scanning: "Scanning",
    signed: "Signed",
    voided: "Voided",
  },
  work: {
    blocked: "Blocked",
    canceled: "Canceled",
    completed: "Completed",
    failed: "Failed",
    inProgress: "In progress",
    open: "Open",
    readyForReview: "Ready for review",
  },
} as const;

type CentralOperationDomain =
  | "arrangementLink"
  | "call"
  | "case"
  | "checklist"
  | "document"
  | "familyPortal"
  | "form"
  | "payment"
  | "signatureLink"
  | "signaturePacket";

type CentralOperationAction = "approve" | "create" | "load" | "send" | "update";

const CENTRAL_OPERATION_ERROR_TITLES: Record<CentralOperationDomain, Partial<Record<CentralOperationAction, string>> & { update: string }> = {
  arrangementLink: { create: "Arrangement link wasn't created", update: "Arrangement link wasn't updated" },
  call: { create: "Call wasn't logged", load: "Calls couldn't load", update: "Call wasn't updated" },
  case: { create: "Case wasn't created", load: "Cases couldn't load", update: "Case wasn't updated" },
  checklist: { create: "Task wasn't added", load: "Checklist couldn't load", update: "Task wasn't updated" },
  document: { create: "Document wasn't added", load: "Documents couldn't load", update: "Document wasn't updated" },
  familyPortal: { create: "Family portal link wasn't issued", load: "Family portal status couldn't load", send: "Family portal message wasn't sent", update: "Family portal wasn't updated" },
  form: { approve: "Form wasn't approved", create: "Form wasn't generated", load: "Forms couldn't load", update: "Form wasn't updated" },
  payment: { create: "Payment wasn't recorded", load: "Financial activity couldn't load", update: "Balance wasn't updated" },
  signatureLink: { create: "Signature link wasn't created", update: "Signature link wasn't updated" },
  signaturePacket: { create: "Signature packet wasn't created", load: "Signature packets couldn't load", send: "Signature packet wasn't sent", update: "Signature packet wasn't updated" },
};

export function centralOperationErrorTitle(domain: CentralOperationDomain, action: CentralOperationAction = "update") {
  return CENTRAL_OPERATION_ERROR_TITLES[domain][action] ?? CENTRAL_OPERATION_ERROR_TITLES[domain].update;
}

export function centralContactPermissionLabel(consents: readonly string[]) {
  if (consents.includes("do_not_contact")) return "Do not contact";
  if (consents.includes("declined")) return "Declined";
  if (!consents.length) return "Permission not requested";
  return consents.map(sentenceCaseToken).join(", ");
}

export function sentenceCaseToken(value: string) {
  const text = value.trim().replaceAll("_", " ");
  return text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : "";
}
